import { Button, Card, Icon, LinkButton } from '@/components/ui';
import { formatSlot, interviewStanding } from '@/features/interviews/reschedule';
import { interviewWhen, whereToGo } from '@/features/interviews/status';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

import { BlockedAction } from './CandidateRestriction';

/** The banner tone for each kind of moment: the company's move, waiting, done, closed. */
const TONES = {
  act: 'bg-brand-50 text-brand-800 ring-brand-100',
  wait: 'bg-info-50 text-info-800 ring-info-100',
  warn: 'bg-warning-50 text-warning-900 ring-warning-200',
  done: 'bg-success-50 text-success-800 ring-success-100',
  closed: 'bg-surface-muted text-ink-muted ring-line',
};

const ICON_TONES = {
  act: 'bg-brand-600 text-white',
  wait: 'bg-info-600 text-white',
  warn: 'bg-warning-500 text-white',
  done: 'bg-success-600 text-white',
  closed: 'bg-ink-muted/20 text-ink-muted',
};

/**
 * What each button needs, matching what the API checks. A button the member
 * cannot use is hidden rather than left to fail with a 403 after they have
 * filled in a form.
 */
const BUTTON_PERMISSIONS = {
  shortlist: [PERMISSIONS.APPLICATION_UPDATE, PERMISSIONS.CANDIDATE_SHORTLIST],
  review: [PERMISSIONS.APPLICATION_UPDATE],
  reject: [PERMISSIONS.APPLICATION_UPDATE, PERMISSIONS.CANDIDATE_REJECT],
  joined: [PERMISSIONS.APPLICATION_UPDATE],
  schedule: [PERMISSIONS.INTERVIEW_CREATE],
  closeRound: [PERMISSIONS.INTERVIEW_UPDATE],
  moveRound: [PERMISSIONS.INTERVIEW_UPDATE],
  cancelRound: [PERMISSIONS.INTERVIEW_UPDATE],
  acceptRequest: [PERMISSIONS.INTERVIEW_UPDATE],
  declineRequest: [PERMISSIONS.INTERVIEW_UPDATE],
  joinCall: [PERMISSIONS.INTERVIEW_CREATE],
  interviews: [PERMISSIONS.INTERVIEW_CREATE],
  offer: [PERMISSIONS.OFFER_CREATE],
  sendOffer: [PERMISSIONS.OFFER_CREATE],
  offers: [PERMISSIONS.OFFER_CREATE],
};

/** The names the Roles screen uses, so the hint matches what an admin ticks. */
const PERMISSION_NAMES = {
  [PERMISSIONS.APPLICATION_UPDATE]: 'Change application status',
  [PERMISSIONS.CANDIDATE_SHORTLIST]: 'Shortlist candidates',
  [PERMISSIONS.CANDIDATE_REJECT]: 'Reject candidates',
  [PERMISSIONS.INTERVIEW_CREATE]: 'Schedule interviews',
  [PERMISSIONS.INTERVIEW_UPDATE]: 'Reschedule, cancel and complete interviews',
  [PERMISSIONS.OFFER_CREATE]: 'Create and send offers',
};

/**
 * What the company should do next with this application, as one card.
 *
 * The status alone decides what is possible (see "What the company can do
 * next" in the backend's docs/api.md); the rounds and offers only fill in the
 * detail -- which round, which offer, when. Every button here is an action that
 * *causes* a move: booking a round, recording how it went, making an offer. The
 * status itself is never picked from a list, so it cannot claim a round or an
 * offer that does not exist.
 */
export function NextStepPanel({
  application,
  rounds,
  offers,
  actions,
  busy = {},
  blockedReason = null,
}) {
  const { can } = usePermissions();
  const step = describe(application, rounds, offers);
  const missing = new Set();
  const buttons = step.buttons.filter((button) => {
    const needed = (BUTTON_PERMISSIONS[button.key] ?? []).filter((code) => !can(code));
    needed.forEach((code) => missing.add(code));
    return needed.length === 0;
  });

  return (
    <Card className="overflow-hidden p-0">
      <div className={cn('flex gap-3 p-5 ring-1 ring-inset', TONES[step.tone])}>
        <span
          aria-hidden="true"
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-xl shadow-sm',
            ICON_TONES[step.tone],
          )}
        >
          <Icon name={step.icon} size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold tracking-wide uppercase opacity-80">
            {step.tone === 'closed' || step.tone === 'done' ? 'Outcome' : 'Next step'}
          </p>
          <h2 className="text-ink mt-0.5 text-base leading-snug font-semibold">{step.title}</h2>
          {step.detail && <p className="text-ink-muted mt-1 text-sm">{step.detail}</p>}
        </div>
      </div>

      {buttons.length > 0 && blockedReason && (
        // The candidate's account is suspended: the steps stay visible, so the
        // team can see where things stood, but none of them can be taken.
        <div className="flex flex-col gap-2 p-5">
          {buttons.map((button) => (
            <BlockedAction key={button.key} reason={blockedReason} className="block">
              <Button variant={button.variant} disabled className="w-full">
                <Icon name={button.icon} size={16} />
                {button.label}
              </Button>
            </BlockedAction>
          ))}
          <p className="text-danger-700 flex gap-2 text-xs">
            <Icon name="suspended" size={14} className="mt-0.5 shrink-0" />
            <span>{blockedReason}</span>
          </p>
        </div>
      )}

      {buttons.length > 0 && !blockedReason && (
        <div className="flex flex-col gap-2 p-5">
          {buttons.map((button) =>
            button.href ? (
              <a
                key={button.key}
                href={button.href}
                target="_blank"
                rel="noreferrer noopener"
                className={EXTERNAL_BUTTON}
              >
                <Icon name={button.icon} size={16} />
                {button.label}
              </a>
            ) : button.to ? (
              <LinkButton key={button.key} to={button.to} variant={button.variant}>
                <Icon name={button.icon} size={16} />
                {button.label}
              </LinkButton>
            ) : (
              <Button
                key={button.key}
                variant={button.variant}
                isLoading={busy[button.key]}
                onClick={() => actions[button.key]?.(button.arg)}
              >
                <Icon name={button.icon} size={16} />
                {button.label}
              </Button>
            ),
          )}
        </div>
      )}

      {missing.size > 0 && (
        <p className="text-ink-muted border-line flex gap-2 border-t px-5 py-3 text-xs">
          <Icon name="info" size={14} className="mt-0.5 shrink-0" />
          <span>
            Some steps are hidden because your role does not include{' '}
            {[...missing].map((code) => PERMISSION_NAMES[code] ?? code).join(', ')}. A company admin
            can grant it under Roles and permissions.
          </span>
        </p>
      )}
    </Card>
  );
}

/** An external link (the meeting) dressed as the panel's tinted button. */
const EXTERNAL_BUTTON =
  'border border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100 ' +
  'rounded-input inline-flex min-h-9 w-full items-center justify-center gap-2 px-4 py-1.5 ' +
  'text-sm font-medium transition';

const REJECT = {
  key: 'reject',
  label: 'Reject',
  icon: 'close',
  variant: 'ghostDanger',
};
// On hand once the candidate has accepted the booked round: the outcome
// recorded here is what leads to an offer, another round or a rejection.
const RECORD = {
  key: 'closeRound',
  label: 'Record the outcome',
  icon: 'clipboard',
  variant: 'outline',
};

function describe(application, rounds, offers) {
  const { status } = application;
  const openRound = rounds.find((round) => round.status === 'SCHEDULED');
  // Rounds are numbered from the highest ever booked, cancelled ones included.
  const nextRound = rounds.reduce((max, round) => Math.max(max, round.round_number), 0) + 1;
  // The highest-numbered closed round, whatever order the list arrives in (the
  // API sends the latest slot first, so taking the last one picked round 1).
  const lastClosed = rounds
    .filter((round) => round.status === 'COMPLETED' || round.status === 'NO_SHOW')
    .reduce(
      (latest, round) => (!latest || round.round_number > latest.round_number ? round : latest),
      null,
    );
  const draft = offers.find((offer) => offer.status === 'DRAFT');
  const sent = offers.find((offer) => offer.status === 'SENT');
  const accepted = offers.find((offer) => offer.status === 'ACCEPTED');

  // An offer drafted but not sent: sending it is the natural next move.
  const offerButton = draft
    ? { key: 'sendOffer', label: 'Send the draft offer', icon: 'send', arg: draft }
    : { key: 'offer', label: 'Make an offer', icon: 'contract' };

  switch (status) {
    case 'SUBMITTED':
      return {
        tone: 'act',
        icon: 'eye',
        title: 'Review this application',
        detail:
          'Read the CV and cover letter, then shortlist the candidate or let them know it is a no.',
        buttons: [
          { key: 'shortlist', label: 'Shortlist', icon: 'userCheck', variant: 'primary' },
          { key: 'review', label: 'Mark as in review', icon: 'eye', variant: 'secondary' },
          REJECT,
        ],
      };
    case 'UNDER_REVIEW':
      return {
        tone: 'act',
        icon: 'eye',
        title: 'Decide whether to take them forward',
        detail: 'Shortlisting lets you invite them to an interview or make a direct offer.',
        buttons: [
          { key: 'shortlist', label: 'Shortlist', icon: 'userCheck', variant: 'primary' },
          REJECT,
        ],
      };
    case 'SHORTLISTED':
      return {
        tone: 'act',
        icon: 'calendarClock',
        title: 'Invite them to a first interview',
        detail: 'Already convinced? You can skip the interview and make a direct offer.',
        buttons: [
          {
            key: 'schedule',
            label: `Schedule round ${nextRound}`,
            icon: 'calendarClock',
            variant: 'primary',
            arg: nextRound,
          },
          {
            ...offerButton,
            label: draft ? offerButton.label : 'Make a direct offer',
            variant: 'outline',
          },
          REJECT,
        ],
      };
    case 'INTERVIEW':
      return openRound ? describeRound(openRound) : BOOKED_FALLBACK;
    case 'DECISION_PENDING':
      return {
        tone: 'act',
        icon: 'flag',
        title: lastClosed
          ? `Round ${lastClosed.round_number} is done — your decision`
          : 'Your decision',
        detail: lastClosed
          ? `${lastClosed.status === 'NO_SHOW' ? 'The candidate did not attend.' : lastClosed.average_rating ? `Panel rating ${lastClosed.average_rating}/5.` : 'The round took place.'} Take them forward with an offer or another round, or close the application.`
          : 'Take them forward with an offer or another round, or close the application.',
        buttons: [
          { ...offerButton, variant: 'primary' },
          {
            key: 'schedule',
            label: `Schedule round ${nextRound}`,
            icon: 'calendarClock',
            variant: 'outline',
            arg: nextRound,
          },
          REJECT,
        ],
      };
    case 'OFFERED':
      return {
        tone: 'wait',
        icon: 'contract',
        title: "Waiting for the candidate's answer",
        detail: sent
          ? `“${sent.title}” was sent. They have until ${formatDate(sent.respond_by)} to accept or decline.`
          : 'An offer is with the candidate.',
        buttons: [
          {
            key: 'offers',
            label: 'Manage offers',
            icon: 'contract',
            variant: 'secondary',
            to: paths.company.offers,
          },
          REJECT,
        ],
      };
    case 'OFFER_ACCEPTED':
      return {
        tone: 'act',
        icon: 'userCheck',
        title: 'Offer accepted — waiting for day one',
        detail: accepted
          ? `They start on ${formatDate(accepted.starts_on)}. Once they have joined, mark them as hired.`
          : 'Once they have joined, mark them as hired.',
        buttons: [
          { key: 'joined', label: 'Mark as joined', icon: 'check', variant: 'success' },
          { ...REJECT, label: 'They did not join' },
        ],
      };
    case 'HIRED':
      return {
        tone: 'done',
        icon: 'check',
        title: 'Joined — hiring complete',
        detail: 'The candidate accepted the offer and started with you.',
        buttons: [],
      };
    case 'REJECTED':
      return {
        tone: 'closed',
        icon: 'close',
        title: 'Not taken forward',
        detail: application.rejection_reason || 'This application is closed.',
        buttons: [],
      };
    default:
      return {
        tone: 'closed',
        icon: 'logout',
        title: 'Withdrawn by the candidate',
        detail: 'The candidate stepped out, or declined the offer. Nothing more to do here.',
        buttons: [],
      };
  }
}

const BOOKED_FALLBACK = {
  tone: 'wait',
  icon: 'calendar',
  title: 'An interview round is booked',
  detail: 'Once it has taken place, record how it went to decide what comes next.',
  buttons: [
    {
      key: 'interviews',
      label: 'Reschedule or cancel',
      icon: 'calendar',
      variant: 'secondary',
      to: paths.company.interviews,
    },
    REJECT,
  ],
};

const MOVE = { key: 'moveRound', label: 'Reschedule', icon: 'calendar', variant: 'secondary' };
const CANCEL = {
  key: 'cancelRound',
  label: 'Cancel interview',
  icon: 'close',
  variant: 'ghostDanger',
};

/**
 * The booked round, by where it stands (`interviewStanding`, shared with the
 * rounds list and the Interviews page so they always agree).
 */
function describeRound(round, now = new Date()) {
  const standing = interviewStanding(round, now);
  const label = `Round ${round.round_number}`;
  const when = interviewWhen(round);

  if (standing.key === 'requested') {
    const request = round.reschedule_request;
    const proposed = request.any_time
      ? 'Any time works for them.'
      : `Proposed: ${request.proposed_slots.map(formatSlot).join(' · ')}.`;
    return {
      tone: 'warn',
      icon: 'repeat',
      title: `${label}: the candidate asked for another time`,
      detail: `“${request.reason}” ${proposed} Accept to move the interview, or decline, which cancels it.`,
      buttons: [
        {
          key: 'declineRequest',
          label: 'Decline',
          icon: 'close',
          variant: 'tintDanger',
          arg: round,
        },
        {
          key: 'acceptRequest',
          label: 'Accept',
          icon: 'check',
          variant: 'tintPositive',
          arg: round,
        },
        REJECT,
      ],
    };
  }

  // Only a round the candidate accepted can have an outcome recorded; one
  // they never answered or declined is rescheduled or cancelled instead.
  if (standing.started && standing.key === 'confirmed') {
    return {
      tone: 'act',
      icon: 'clipboard',
      title: `${label}: record how it went`,
      detail: `${when}. Record the outcome to decide what comes next.`,
      buttons: [
        {
          key: 'closeRound',
          label: 'Record the outcome',
          icon: 'clipboard',
          variant: 'primary',
          arg: round,
        },
        REJECT,
      ],
    };
  }

  if (standing.key === 'declined') {
    return {
      tone: 'warn',
      icon: 'alert',
      title: `${label}: the candidate can’t make it`,
      detail: `${standing.detail ? `“${standing.detail}” ` : ''}Choose a new time (they will be asked to confirm it) or cancel the interview.`,
      buttons: [{ ...MOVE, variant: 'primary', arg: round }, { ...CANCEL, arg: round }, REJECT],
    };
  }

  if (standing.key === 'confirmed') {
    const joinUrl = round.mode === 'VIDEO' ? round.meeting_url : null;
    return {
      tone: 'done',
      icon: 'check',
      title: `${label} is confirmed by the candidate`,
      detail: `${when} · ${whereToGo(round)}`,
      buttons: [
        joinUrl
          ? { key: 'joinCall', label: 'Join the call', icon: 'external', href: joinUrl }
          : {
              key: 'joinCall',
              label: 'Interview details',
              icon: 'calendar',
              variant: 'secondary',
              to: paths.company.interviews,
            },
        { ...RECORD, arg: round },
        { ...MOVE, arg: round },
        REJECT,
      ],
    };
  }

  // The slot came and went unanswered -- including a request for another time
  // nobody got to, which lapses with it. The candidate can no longer answer
  // this time, so the next move is a new one (which asks them again).
  if (standing.started) {
    return {
      tone: 'warn',
      icon: 'alert',
      title: `${label}: the time passed without an answer`,
      detail: `${when}. The candidate did not confirm this time. Choose a new time (they will be asked to confirm it) or cancel the interview.`,
      buttons: [{ ...MOVE, variant: 'primary', arg: round }, { ...CANCEL, arg: round }, REJECT],
    };
  }

  return {
    tone: 'wait',
    icon: 'clock',
    title: `${label}: waiting for the candidate’s response`,
    detail: `${when}. Invitation sent ${formatDate(round.created_at)}. You can still reschedule or cancel.`,
    buttons: [{ ...MOVE, arg: round }, { ...CANCEL, arg: round }, REJECT],
  };
}
